import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { isEarlyAnnualTierIIReport } from "encamp-shared/src/tierii/earlyAnnualReport";
import { DateTime } from "luxon";

export type ReportForDefaultDate = {
  reportKind?: string | null;
  reportingYear?: number | null;
  facilityState?: string | null;
};

/** Early-annual check against the viewer's local calendar date, matching the date field. */
function isEarlyAnnualReport(
  report: ReportForDefaultDate,
  now: DateTime,
  currentReportingYear: number
): boolean {
  const today = now.toISODate();
  return today !== null && isEarlyAnnualTierIIReport(report, today, currentReportingYear);
}

/**
 * Default signature date for the verify dialog: January 1 of next year when every
 * selected report is an early annual (all accept it), otherwise today.
 */
export function defaultVerifiedAtDate(
  reports: ReportForDefaultDate[],
  now: DateTime = DateTime.now(),
  reportingYear: number = currentTierIIReportingYear
): string {
  const yearEndIso = `${reportingYear + 1}-01-01`;
  const allEarly =
    reports.length > 0 &&
    reports.every((report) => isEarlyAnnualReport(report, now, reportingYear));
  if (allEarly) {
    return yearEndIso;
  }
  return now.toISODate() ?? yearEndIso;
}

/** Why the signature date can't be used for every selected report, or null when it can. */
export function signatureDateConflict(
  reports: ReportForDefaultDate[],
  dateIso: string | null | undefined,
  now: DateTime = DateTime.now(),
  currentReportingYear: number = currentTierIIReportingYear
): string | null {
  const yearEnd = `${currentReportingYear + 1}-01-01`;
  const earlyFlags = reports.map((report) =>
    isEarlyAnnualReport(report, now, currentReportingYear)
  );
  const hasEarlyNonCa = reports.some((report, i) => earlyFlags[i] && report.facilityState !== "CA");
  const hasNonEarly = earlyFlags.some((isEarly) => !isEarly);
  if (hasEarlyNonCa && hasNonEarly) {
    return `These reports need different signature dates: early annual reports are signed January 1, ${currentReportingYear + 1}, and the others can't be dated in the future. Verify them in separate batches.`;
  }
  if (!dateIso) {
    return null;
  }
  if (hasEarlyNonCa && dateIso !== yearEnd) {
    return `The signature date for an early annual report must be January 1, ${currentReportingYear + 1}.`;
  }
  const today = now.toISODate();
  // A future date is only valid as January 1 next year, when every report is an early annual.
  if (today !== null && dateIso > today && (hasNonEarly || dateIso !== yearEnd)) {
    return "The signature date can't be in the future.";
  }
  return null;
}
